// Imports
import ___CSS_LOADER_API_SOURCEMAP_IMPORT___ from "../../node_modules/css-loader/dist/runtime/sourceMaps.js";
import ___CSS_LOADER_API_IMPORT___ from "../../node_modules/css-loader/dist/runtime/api.js";
var ___CSS_LOADER_EXPORT___ = ___CSS_LOADER_API_IMPORT___(___CSS_LOADER_API_SOURCEMAP_IMPORT___);
// Module
___CSS_LOADER_EXPORT___.push([module.id, `/* ════════════════════════════════════════════════════════════════════════
   Top app bar

   SubPageHeader (App.js) renders, in source order:

     .sub-page-header-inner
       .sph-left   → logo mark, "AcyMart", the user's first name, "›", title
       .sph-right  → user name again, theme toggle, [← Back] pill

   On the web that's a branded sub-header. On a phone it reads as a wasted
   72px strip with the primary navigation control stranded in the far corner
   and the title buried behind three pieces of branding.

   Rather than fork the component, this restyles it into a standard app bar —
   back affordance at the leading edge, one title, actions trailing:

     [←]  Order History                              [◑]

   \`display: contents\` on .sph-right dissolves that wrapper so its children
   become flex items of the same row as .sph-left, which is what lets \`order\`
   pull the back button ahead of the title without touching the JSX.
   ════════════════════════════════════════════════════════════════════════ */

html.m-app .sub-page-header {
  position: sticky;
  top: 0;
  z-index: 120;
  background: var(--m-surface-bar);
  border-bottom: 1px solid var(--m-hairline);
  box-shadow: none;
  /* Claims the status-bar strip in the packaged app so the bar's content
     starts below the clock, and the bar's own background paints behind it. */
  padding-top: var(--m-safe-top);
  padding-inline: 0;
}

@supports (backdrop-filter: blur(12px)) {
  html.m-app .sub-page-header {
    background: color-mix(in srgb, var(--m-surface-bar) 82%, transparent);
    backdrop-filter: saturate(180%) blur(16px);
    -webkit-backdrop-filter: saturate(180%) blur(16px);
  }
}

html.m-app .sub-page-header-inner {
  display: flex;
  align-items: center;
  gap: var(--m-1);
  min-height: var(--m-appbar);
  max-width: none;
  margin: 0;
  padding: 0 var(--m-2) 0 var(--m-1);
  padding-left: max(var(--m-1), var(--m-safe-left));
  padding-right: max(var(--m-2), var(--m-safe-right));
}

/* Dissolve the right-hand group so back / theme / title share one flex row. */
html.m-app .sph-right {
  display: contents;
}

/* ── Back: the leading icon button ────────────────────────────────────── */
html.m-app .sph-back {
  order: 1;
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: var(--m-touch);
  height: var(--m-touch);
  min-width: var(--m-touch);
  min-height: var(--m-touch);
  padding: 0;
  gap: 0;
  border: none;
  border-radius: var(--m-r-pill);
  background: transparent;
  color: var(--color-text-primary);
  /* Collapses the "Back" / "Back to Cart" text label without hiding the
     inline <svg>, which keeps the accessible name intact for screen
     readers while the icon alone carries it visually. */
  font-size: 0;
  line-height: 0;
  white-space: nowrap;
  overflow: hidden;
}

html.m-app .sph-back svg {
  width: var(--m-icon-nav);
  height: var(--m-icon-nav);
  stroke-width: 2;
  flex: 0 0 auto;
}

html.m-app .sph-back:active {
  background: var(--color-surface-hover);
}

/* ── Title ──────────────────────────────────────────────────────────────── */
html.m-app .sph-left {
  order: 2;
  display: flex;
  align-items: center;
  min-width: 0;      /* lets the title ellipsis instead of pushing actions off */
  flex: 1 1 auto;
  gap: 0;
  margin: 0;
  padding: 0;
}

/* Branding and the breadcrumb machinery have no job here: the app icon is on
   the home screen, and the user's name is one tap away under Account. */
html.m-app .sph-logo-icon-wrap,
html.m-app .sph-logo-name,
html.m-app .sph-sep,
html.m-app .sph-mobile-user,
html.m-app .sph-mobile-username,
html.m-app .sph-user {
  display: none;
}

html.m-app .sph-title {
  font-size: 1.0625rem;             /* 17px — the app-bar size, between  */
  font-weight: var(--m-fw-semibold); /* card title and page title        */
  line-height: var(--m-lh-tight);
  letter-spacing: var(--m-track-title);
  color: var(--color-text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
  /* Optical alignment: sits 2px in from the back button's icon so title and
     content below share a left edge. */
  padding-left: var(--m-1);
}

/* When there's a back button the title indents past it; when there isn't,
   it takes the full gutter itself. */
html.m-app .sub-page-header-inner:not(:has(.sph-back)) .sph-left {
  padding-left: var(--m-3);
}

/* ── Trailing actions ───────────────────────────────────────────────────── */
html.m-app .sph-theme {
  order: 3;
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: var(--m-touch);
  height: var(--m-touch);
  min-width: var(--m-touch);
  padding: 0;
  border: none;
  border-radius: var(--m-r-pill);
  background: transparent;
  color: var(--color-text-secondary);
}

html.m-app .sph-theme:active {
  background: var(--color-surface-hover);
}

html.m-app .sph-theme svg {
  width: var(--m-icon); height: var(--m-icon);
}

/* \`hideThemeOnMobile\` is passed on most sub-pages; honour it so the bar
   carries a back affordance and a title and nothing else. */
html.m-app .sph-theme-hide-mobile {
  display: none;
}

html.m-app .sub-page-header-flush {
  border-bottom: none;
}

/* ════════════════════════════════════════════════════════════════════════
   Storefront header

   The home screen's header is a different component (.site-header) carrying
   an announcement bar, a logo/tagline lockup, a search field with a category
   <select> welded into it, and a row of icon actions — roughly 190px of
   chrome before any product. Compressed onto a phone it pushed the first
   product below the fold.

   Mobile keeps exactly two things in the bar: identity (so you know where
   you are) and search (the primary way anyone finds anything in a store).
   ════════════════════════════════════════════════════════════════════════ */

html.m-app .site-header {
  position: sticky;
  top: 0;
  z-index: 120;
}

/* The announcement bar wrapped to two lines and ate 100px. It carries real
   information (flash sale, free-delivery threshold), so it stays — but as a
   single non-wrapping marquee line, and never inside the packaged app where
   there's no room to spare. */
html.m-app .ann-bar {
  padding-top: var(--m-safe-top);
  background: var(--color-brand-navy);
}

html.m-app .ann-bar-inner {
  min-height: 30px;
  padding: 0 var(--m-gutter);
  gap: var(--m-2);
}

html.m-app .ann-text {
  font-size: var(--m-fs-caption);
  line-height: 1.2;
  font-weight: var(--m-fw-medium);
  color: var(--color-on-navy);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin: 0;
}

html.m-app .ann-countdown {
  font-variant-numeric: tabular-nums;
  font-weight: var(--m-fw-semibold);
}

/* Sign-in lives in the Account tab; a second entry point here competes with
   it and costs width the sale message needs. */
html.m-app .ann-right,
html.m-app .ann-auth-btn {
  display: none;
}

/* .m-installed, not .m-native: an installed PWA is launched from the same home
   screen with the same no-browser-chrome framing, so the announcement bar is
   just as out of place there as in the packaged app. */
html.m-installed .ann-bar {
  display: none;
}

html.m-app .main-navbar {
  background: var(--m-surface-bar);
  border-bottom: 1px solid var(--m-hairline);
  box-shadow: none;
  padding: 0;
}

html.m-app .nb-top {
  display: flex;
  align-items: center;
  gap: var(--m-2);
  min-height: var(--m-appbar);
  padding: var(--m-2) var(--m-gutter);
  max-width: none;
}

html.m-app .nb-logo {
  flex: 0 0 auto;
  gap: var(--m-2);
  padding: 0;
  min-height: 0;
}

/* The tagline is brand copy for a wide header; on a phone it's a second line
   of text competing with the search field for the same row. */
html.m-app .nb-logo-tagline {
  display: none;
}

html.m-app .nb-logo-name {
  font-size: var(--m-fs-card-title);
  font-weight: var(--m-fw-bold);
  letter-spacing: var(--m-track-title);
  line-height: 1;
}

html.m-app .nb-logo svg {
  width: 30px;
  height: 30px;
}

/* Collapsed search: an icon button that expands the field over the bar. */
html.m-app .nb-mobile-search-btn {
  order: 5;
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: var(--m-touch);
  height: var(--m-touch);
  min-width: var(--m-touch);
  padding: 0;
  border: none;
  border-radius: var(--m-r-pill);
  background: var(--m-surface-sunken);
  color: var(--color-text-primary);
}

html.m-app .nb-mobile-search-btn:active {
  background: var(--color-surface-hover);
}

html.m-app .nb-actions {
  order: 6;
  display: flex;
  align-items: center;
  gap: var(--m-1);
  margin-left: auto;
}

/* Cart, wishlist and account all have bottom-nav tabs — duplicating them in
   the top bar is the single biggest source of "too many actions competing"
   on this screen. Theme and language stay: neither has another home. */
html.m-app .nb-actions > .nb-cart-btn,
html.m-app .nb-actions > .nb-wishlist-btn,
html.m-app .nb-actions > .nb-account-wrap {
  display: none;
}

html.m-app .nb-actions button {
  width: var(--m-touch);
  height: var(--m-touch);
  min-width: var(--m-touch);
  padding: 0;
  border-radius: var(--m-r-pill);
}

/* ── Search field ───────────────────────────────────────────────────────── */
html.m-app .nb-search-wrap {
  order: 4;
  flex: 1 1 auto;
  min-width: 0;
}

/* The field itself stays collapsed until the search icon expands it — that
   behaviour already exists in App.css and is the right one, so \`display\` is
   deliberately NOT set here. Setting it would show the field and the icon
   button that opens it at the same time, which is how this bar ended up
   with two search affordances side by side. Only the appearance of the
   expanded state is restyled. */
html.m-app .nb-search-bar {
  align-items: center;
  gap: var(--m-2);
  height: var(--m-touch);
  padding: 0 var(--m-3);
  border: 1px solid var(--m-hairline);
  border-radius: var(--m-r-pill);
  background: var(--m-surface-sunken);
  box-shadow: none;
}

html.m-app .nb-search-bar:focus-within {
  border-color: var(--color-secondary);
  background: var(--m-surface-card);
}

html.m-app .nb-sb-input {
  flex: 1 1 auto;
  min-width: 0;
  height: 100%;
  border: none;
  background: transparent;
  /* 16px exactly: iOS Safari zooms the viewport on focus for anything
     smaller, and never zooms back out. */
  font-size: 1rem;
  color: var(--color-text-primary);
  padding: 0;
}

html.m-app .nb-sb-input::placeholder {
  color: var(--color-text-muted);
  font-size: var(--m-fs-body);
}

html.m-app .nb-sb-search-icon {
  flex: 0 0 auto;
  color: var(--color-text-muted);
  width: var(--m-icon); height: var(--m-icon);
}

/* The welded-in category <select>, its divider, its caret and the submit
   button are a desktop compound control. On mobile, category selection is
   the job of the category rail below the bar and the Categories tab, and
   the keyboard's own "Search" key submits. */
html.m-app .nb-sb-cat,
html.m-app .nb-sb-divider,
html.m-app .nb-sb-caret,
html.m-app .nb-sb-btn {
  display: none;
}

html.m-app .nb-sb-clear {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  min-height: 22px;
  min-width: 22px;
  padding: 0;
  border: none;
  border-radius: var(--m-r-pill);
  background: var(--color-surface-hover);
  color: var(--color-text-secondary);
}

/* Expanded search takes the whole bar — the standard mobile search overlay,
   with the back chevron returning to browse. */
html.m-app .nb-search-expanded {
  background: var(--m-surface-card);
}

html.m-app .nb-sb-back {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  min-height: 32px;
  min-width: 32px;
  margin-left: calc(var(--m-2) * -1);
  padding: 0;
  border: none;
  background: transparent;
  color: var(--color-text-primary);
}

/* ── Search suggestions ─────────────────────────────────────────────────
   A dropdown panel becomes a full-width results list flush under the bar. */
html.m-app .nb-autocomplete {
  position: absolute;
  left: calc(var(--m-gutter) * -1);
  right: calc(var(--m-gutter) * -1);
  top: calc(100% + var(--m-2));
  max-height: 60vh;
  overflow-y: auto;
  border: none;
  border-top: 1px solid var(--m-hairline);
  border-radius: 0;
  box-shadow: var(--m-shadow-raised);
  background: var(--m-surface-card);
  padding: var(--m-1) 0;
}

html.m-app .nb-ac-item {
  display: flex;
  align-items: center;
  gap: var(--m-3);
  width: 100%;
  min-height: 56px;
  padding: var(--m-2) var(--m-gutter);
  border: none;
  background: transparent;
  text-align: left;
}

html.m-app .nb-ac-item:active {
  background: var(--color-surface-hover);
}

html.m-app .nb-ac-img {
  width: 40px;
  height: 40px;
  border-radius: var(--m-r-xs);
  object-fit: cover;
  flex: 0 0 auto;
}

html.m-app .nb-ac-info {
  min-width: 0;
  flex: 1 1 auto;
}

html.m-app .nb-ac-name {
  display: block;
  font-size: var(--m-fs-body);
  font-weight: var(--m-fw-medium);
  line-height: var(--m-lh-snug);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

html.m-app .nb-ac-cat {
  font-size: var(--m-fs-caption);
  color: var(--color-text-muted);
}

html.m-app .nb-ac-price {
  flex: 0 0 auto;
  font-size: var(--m-fs-secondary);
  font-weight: var(--m-fw-semibold);
  font-variant-numeric: tabular-nums;
}

/* ── Storefront stats bar: hidden on mobile ─────────────────────────────
   "4+ Verified Sellers · 98% Positive Reviews · 140 Total Reviews · Fast
   Delivery" is social proof aimed at a first-time desktop visitor. On a
   phone it costs 138px above the product grid — most of a screen — and it
   is the only element in the app that runs an infinite marquee animation,
   which spends battery for something nobody scrolls back up to read.

   \`display: none\` on the wrapper takes the animation with it, so the
   marquee stops running rather than merely being off-screen. The markup
   still renders for the desktop build, which is untouched. */
html.m-app .shop-stats-bar {
  display: none;
}

/* ── Search button in the top bar ───────────────────────────────────────
   All four top-bar actions are 44x44 for touch, but this one carried a
   filled circle while its siblings (language, theme) are bare icons — so
   it read as a big grey blob next to two small glyphs.

   The fill comes off and the icon settles at the same 20px as its
   neighbours, which is what actually makes it look smaller. The 44px box
   stays: it is the tap target, and with no background it is invisible, so
   shrinking it would cost accuracy and buy nothing visually. */
html.m-app .nb-mobile-search-btn {
  background: transparent;
  border: none;
}

html.m-app .nb-mobile-search-btn:active {
  background: var(--color-surface-hover);
}

/* One icon size across the whole action row, so search, language and theme
   sit on a common optical weight. */
html.m-app .nb-mobile-search-btn svg,
html.m-app .nb-actions button svg {
  width: 20px;
  height: 20px;
}

/* ════════════════════════════════════════════════════════════════════════
   Bottom navigation: labels and icons in black

   An explicit, requested exception to this layer's rule of leaving
   BottomNav alone. The geometry fence above still stands -- only colour
   changes here, nothing that affects size or spacing.

   --color-text-primary rather than a literal black: it is navy (#010D21) in
   light mode, which is the black being asked for, and flips to white in dark
   mode. A hard-coded black would be invisible against the dark bar, and the
   four-colour system has no separate black to reach for anyway.

   The icons inherit this: BottomNav renders its SVGs with stroke and fill
   set to currentColor, so setting the item's colour moves both.

   The active tab used to be the only coloured thing in the bar, which is how
   it announced itself. With everything the same colour that cue is gone, so
   it moves onto weight -- and the Home tab already swaps to a filled icon
   when active, so the state is carried by shape as well as weight rather
   than by colour alone.
   ════════════════════════════════════════════════════════════════════════ */

html.m-app .bn-item,
html.m-app .bn-item.active {
  color: var(--color-text-primary);
}

html.m-app .bn-item.active .bn-label {
  font-weight: 700;
}

/* ════════════════════════════════════════════════════════════════════════
   Separating the app bar from the mobile top bar

   Two different components sit back to back on the storefront:

     .main-navbar   the app bar -- logo, search, language, theme
     .mtb-wrap      the mobile top bar -- delivery address, category rail

   They are already siblings in the DOM, but visually they were one object:
   .main-navbar ended at y=91 and .mtb-wrap began at y=91, both pure white,
   with hairlines between them that read as internal dividers of a single
   201px slab rather than as the edges of two separate things.

   The fix is a band of page canvas between them. The app bar keeps a shadow
   so it reads as fixed chrome; the top bar becomes a block of page content
   that happens to sit directly under it -- which is also what it behaves
   like, since the app bar is sticky and the top bar scrolls away.
   ════════════════════════════════════════════════════════════════════════ */

html.m-app .main-navbar {
  border-bottom: 1px solid var(--m-hairline);
  box-shadow: var(--m-shadow-sm);
}

html.m-app .mtb-wrap {
  /* The gap is the separation -- page canvas showing through between the
     two surfaces. */
  margin-top: var(--m-2);
  border-top: none;
  border-bottom: 1px solid var(--m-hairline);
  background: var(--m-surface-card);
}

/* The delivery row is the top edge of that block now, so its own top border
   would double up with the block's. */
html.m-app .mtb-delivery-row {
  border-top: none;
}

/* And the category rail is the bottom edge, so the same applies there. */
html.m-app .mtb-cat-strip {
  border-bottom: none;
}

/* ════════════════════════════════════════════════════════════════════════
   Persistent search field + condensed header on scroll

   Previously the phone header hid the search behind a magnifier that
   expanded it over the bar. That is one tap between a shopper and the single
   most-used control in a store. The field is now always present, on its own
   full-width row beneath the logo -- the shape in the reference.

   On scroll past the logo (see initCondensedHeader in mobileUI.js) the logo
   row folds away and the search field becomes the sticky header by itself.
   The logo is not sticky, which is the point: it has already done its job by
   the time you are three screens into a product list.
   ════════════════════════════════════════════════════════════════════════ */

/* The bar becomes two rows: logo + actions, then the search field. */
html.m-app .nb-top {
  flex-wrap: wrap;
  row-gap: var(--m-2);
  padding-bottom: var(--m-3);
}

/* Always shown -- App.css hides it below 768px until expanded, which is the
   behaviour being replaced here. */
html.m-app .nb-search-bar {
  display: flex;
}

/* The magnifier that used to expand the field, and the back chevron inside
   the expanded state, are both redundant once the field is permanent. */
html.m-app .nb-mobile-search-btn,
html.m-app .nb-sb-back {
  display: none;
}

/* Full width, second row. */
html.m-app .nb-search-wrap {
  order: 10;
  flex: 1 0 100%;
  min-width: 100%;
}

html.m-app .nb-search-bar {
  height: var(--m-control);
  border-radius: var(--m-r-sm);
  border-color: var(--m-hairline-strong);
  background: var(--m-surface-card);
}

html.m-app .nb-sb-input::placeholder {
  color: var(--color-text-muted);
}

/* ── Condensed state ────────────────────────────────────────────────────
   Height/opacity rather than display:none so it folds instead of jumping,
   and so the transition has something to animate. */
html.m-app.m-condensed .nb-logo,
html.m-app.m-condensed .nb-actions {
  height: 0;
  min-height: 0;
  margin: 0;
  padding: 0;
  opacity: 0;
  overflow: hidden;
  pointer-events: none;
  transition: height var(--m-dur) var(--m-ease), opacity var(--m-dur-fast) var(--m-ease);
}

html.m-app.m-condensed .nb-top {
  row-gap: 0;
  padding-top: var(--m-2);
  padding-bottom: var(--m-2);
}

/* The announcement bar is the other thing worth its space only at rest. */
html.m-app.m-condensed .ann-bar {
  height: 0;
  padding-top: 0;
  overflow: hidden;
  opacity: 0;
}

/* The category rail stays: it is navigation, and it is what makes the
   condensed header useful rather than just smaller. */
html.m-app.m-condensed .mtb-wrap {
  margin-top: 0;
}

@media (prefers-reduced-motion: reduce) {
  html.m-app.m-condensed .nb-logo,
  html.m-app.m-condensed .nb-actions {
    transition: none;
  }
}

/* The field inside the search bar must not draw its own box.
   forms.css styles \`input[type="search"]\` generically, and that selector --
   html + .m-app + input[type=search] -- scores (0,2,2), which outranks a
   plain \`.nb-sb-input\` at (0,2,1). The result was a bordered input nested
   inside the bordered bar: two rounded rectangles, one inside the other,
   with the placeholder pushed in past the magnifier. Scoping to the bar
   raises this to (0,3,1) so the field is flat again. */
html.m-app .nb-search-bar .nb-sb-input,
html.m-app .nb-search-bar input[type="search"] {
  border: none;
  border-radius: 0;
  background: transparent;
  box-shadow: none;
  padding: 0;
  height: 100%;
  min-height: 0;
}

html.m-app .nb-search-bar .nb-sb-input:focus,
html.m-app .nb-search-bar input[type="search"]:focus {
  border: none;
  box-shadow: none;
}

/* Keep the magnifier visible at the leading edge of the bar. */
html.m-app .nb-search-bar .nb-sb-search-icon {
  flex: 0 0 auto;
  display: inline-flex;
  width: var(--m-icon);
  height: var(--m-icon);
  color: var(--color-text-muted);
}
`, "",{"version":3,"sources":["webpack://./src/mobile/appbar.css"],"names":[],"mappings":"AAAA;;;;;;;;;;;;;;;;;;;;;6EAqB6E;;AAE7E;EACE,gBAAgB;EAChB,MAAM;EACN,YAAY;EACZ,gCAAgC;EAChC,0CAA0C;EAC1C,gBAAgB;EAChB;6EAC2E;EAC3E,8BAA8B;EAC9B,iBAAiB;AACnB;;AAEA;EACE;IACE,qEAAqE;IACrE,0CAA0C;IAC1C,kDAAkD;EACpD;AACF;;AAEA;EACE,aAAa;EACb,mBAAmB;EACnB,eAAe;EACf,2BAA2B;EAC3B,eAAe;EACf,SAAS;EACT,kCAAkC;EAClC,iDAAiD;EACjD,mDAAmD;AACrD;;AAEA,8EAA8E;AAC9E;EACE,iBAAiB;AACnB;;AAEA,4EAA4E;AAC5E;EACE,QAAQ;EACR,cAAc;EACd,oBAAoB;EACpB,mBAAmB;EACnB,uBAAuB;EACvB,qBAAqB;EACrB,sBAAsB;EACtB,yBAAyB;EACzB,0BAA0B;EAC1B,UAAU;EACV,MAAM;EACN,YAAY;EACZ,8BAA8B;EAC9B,uBAAuB;EACvB,gCAAgC;EAChC;;wDAEsD;EACtD,YAAY;EACZ,cAAc;EACd,mBAAmB;EACnB,gBAAgB;AAClB;;AAEA;EACE,wBAAwB;EACxB,yBAAyB;EACzB,eAAe;EACf,cAAc;AAChB;;AAEA;EACE,sCAAsC;AACxC;;AAEA,8EAA8E;AAC9E;EACE,QAAQ;EACR,aAAa;EACb,mBAAmB;EACnB,YAAY,OAAO,2DAA2D;EAC9E,cAAc;EACd,MAAM;EACN,SAAS;EACT,UAAU;AACZ;;AAEA;wEACwE;AACxE;;;;;;EAME,aAAa;AACf;;AAEA;EACE,oBAAoB,cAAc,sCAAsC;EACxE,iCAAiC,EAAE,qCAAqC;EACxE,8BAA8B;EAC9B,oCAAoC;EACpC,gCAAgC;EAChC,mBAAmB;EACnB,gBAAgB;EAChB,uBAAuB;EACvB,YAAY;EACZ;uCACqC;EACrC,wBAAwB;AAC1B;;AAEA;qCACqC;AACrC;EACE,wBAAwB;AAC1B;;AAEA,8EAA8E;AAC9E;EACE,QAAQ;EACR,cAAc;EACd,oBAAoB;EACpB,mBAAmB;EACnB,uBAAuB;EACvB,qBAAqB;EACrB,sBAAsB;EACtB,yBAAyB;EACzB,UAAU;EACV,YAAY;EACZ,8BAA8B;EAC9B,uBAAuB;EACvB,kCAAkC;AACpC;;AAEA;EACE,sCAAsC;AACxC;;AAEA;EACE,oBAAoB,EAAE,qBAAqB;AAC7C;;AAEA;4DAC4D;AAC5D;EACE,aAAa;AACf;;AAEA;EACE,mBAAmB;AACrB;;AAEA;;;;;;;;;;;6EAW6E;;AAE7E;EACE,gBAAgB;EAChB,MAAM;EACN,YAAY;AACd;;AAEA;;;8BAG8B;AAC9B;EACE,8BAA8B;EAC9B,mCAAmC;AACrC;;AAEA;EACE,gBAAgB;EAChB,0BAA0B;EAC1B,eAAe;AACjB;;AAEA;EACE,8BAA8B;EAC9B,gBAAgB;EAChB,+BAA+B;EAC/B,2BAA2B;EAC3B,mBAAmB;EACnB,gBAAgB;EAChB,uBAAuB;EACvB,SAAS;AACX;;AAEA;EACE,kCAAkC;EAClC,iCAAiC;AACnC;;AAEA;+CAC+C;AAC/C;;EAEE,aAAa;AACf;;AAEA;;uDAEuD;AACvD;EACE,aAAa;AACf;;AAEA;EACE,gCAAgC;EAChC,0CAA0C;EAC1C,gBAAgB;EAChB,UAAU;AACZ;;AAEA;EACE,aAAa;EACb,mBAAmB;EACnB,eAAe;EACf,2BAA2B;EAC3B,mCAAmC;EACnC,eAAe;AACjB;;AAEA;EACE,cAAc;EACd,eAAe;EACf,UAAU;EACV,aAAa;AACf;;AAEA;8DAC8D;AAC9D;EACE,aAAa;AACf;;AAEA;EACE,iCAAiC;EACjC,6BAA6B;EAC7B,oCAAoC;EACpC,cAAc;AAChB;;AAEA;EACE,WAAW;EACX,YAAY;AACd;;AAEA,0EAA0E;AAC1E;EACE,QAAQ;EACR,cAAc;EACd,oBAAoB;EACpB,mBAAmB;EACnB,uBAAuB;EACvB,qBAAqB;EACrB,sBAAsB;EACtB,yBAAyB;EACzB,UAAU;EACV,YAAY;EACZ,8BAA8B;EAC9B,mCAAmC;EACnC,gCAAgC;AAClC;;AAEA;EACE,sCAAsC;AACxC;;AAEA;EACE,QAAQ;EACR,aAAa;EACb,mBAAmB;EACnB,eAAe;EACf,iBAAiB;AACnB;;AAEA;;uEAEuE;AACvE;;;EAGE,aAAa;AACf;;AAEA;EACE,qBAAqB;EACrB,sBAAsB;EACtB,yBAAyB;EACzB,UAAU;EACV,8BAA8B;AAChC;;AAEA,8EAA8E;AAC9E;EACE,QAAQ;EACR,cAAc;EACd,YAAY;AACd;;AAEA;;;;;gCAKgC;AAChC;EACE,mBAAmB;EACnB,eAAe;EACf,sBAAsB;EACtB,qBAAqB;EACrB,mCAAmC;EACnC,8BAA8B;EAC9B,mCAAmC;EACnC,gBAAgB;AAClB;;AAEA;EACE,oCAAoC;EACpC,iCAAiC;AACnC;;AAEA;EACE,cAAc;EACd,YAAY;EACZ,YAAY;EACZ,YAAY;EACZ,uBAAuB;EACvB;yCACuC;EACvC,eAAe;EACf,gCAAgC;EAChC,UAAU;AACZ;;AAEA;EACE,8BAA8B;EAC9B,2BAA2B;AAC7B;;AAEA;EACE,cAAc;EACd,8BAA8B;EAC9B,oBAAoB,EAAE,qBAAqB;AAC7C;;AAEA;;;6CAG6C;AAC7C;;;;EAIE,aAAa;AACf;;AAEA;EACE,cAAc;EACd,oBAAoB;EACpB,mBAAmB;EACnB,uBAAuB;EACvB,WAAW;EACX,YAAY;EACZ,gBAAgB;EAChB,eAAe;EACf,UAAU;EACV,YAAY;EACZ,8BAA8B;EAC9B,sCAAsC;EACtC,kCAAkC;AACpC;;AAEA;+CAC+C;AAC/C;EACE,iCAAiC;AACnC;;AAEA;EACE,cAAc;EACd,oBAAoB;EACpB,mBAAmB;EACnB,uBAAuB;EACvB,WAAW;EACX,YAAY;EACZ,gBAAgB;EAChB,eAAe;EACf,kCAAkC;EAClC,UAAU;EACV,YAAY;EACZ,uBAAuB;EACvB,gCAAgC;AAClC;;AAEA;4EAC4E;AAC5E;EACE,kBAAkB;EAClB,gCAAgC;EAChC,iCAAiC;EACjC,4BAA4B;EAC5B,gBAAgB;EAChB,gBAAgB;EAChB,YAAY;EACZ,uCAAuC;EACvC,gBAAgB;EAChB,kCAAkC;EAClC,iCAAiC;EACjC,qBAAqB;AACvB;;AAEA;EACE,aAAa;EACb,mBAAmB;EACnB,eAAe;EACf,WAAW;EACX,gBAAgB;EAChB,mCAAmC;EACnC,YAAY;EACZ,uBAAuB;EACvB,gBAAgB;AAClB;;AAEA;EACE,sCAAsC;AACxC;;AAEA;EACE,WAAW;EACX,YAAY;EACZ,4BAA4B;EAC5B,iBAAiB;EACjB,cAAc;AAChB;;AAEA;EACE,YAAY;EACZ,cAAc;AAChB;;AAEA;EACE,cAAc;EACd,2BAA2B;EAC3B,+BAA+B;EAC/B,6BAA6B;EAC7B,mBAAmB;EACnB,gBAAgB;EAChB,uBAAuB;AACzB;;AAEA;EACE,8BAA8B;EAC9B,8BAA8B;AAChC;;AAEA;EACE,cAAc;EACd,gCAAgC;EAChC,iCAAiC;EACjC,kCAAkC;AACpC;;AAEA;;;;;;;;;6DAS6D;AAC7D;EACE,aAAa;AACf;;AAEA;;;;;;;;+DAQ+D;AAC/D;EACE,uBAAuB;EACvB,YAAY;AACd;;AAEA;EACE,sCAAsC;AACxC;;AAEA;oCACoC;AACpC;;EAEE,WAAW;EACX,YAAY;AACd;;AAEA;;;;;;;;;;;;;;;;;;;;6EAoB6E;;AAE7E;;EAEE,gCAAgC;AAClC;;AAEA;EACE,gBAAgB;AAClB;;AAEA;;;;;;;;;;;;;;;;;6EAiB6E;;AAE7E;EACE,0CAA0C;EAC1C,8BAA8B;AAChC;;AAEA;EACE;oBACkB;EAClB,sBAAsB;EACtB,gBAAgB;EAChB,0CAA0C;EAC1C,iCAAiC;AACnC;;AAEA;sCACsC;AACtC;EACE,gBAAgB;AAClB;;AAEA,yEAAyE;AACzE;EACE,mBAAmB;AACrB;;AAEA;;;;;;;;;;;;6EAY6E;;AAE7E,qEAAqE;AACrE;EACE,eAAe;EACf,mBAAmB;EACnB,0BAA0B;AAC5B;;AAEA;mCACmC;AACnC;EACE,aAAa;AACf;;AAEA;wEACwE;AACxE;;EAEE,aAAa;AACf;;AAEA,4BAA4B;AAC5B;EACE,SAAS;EACT,cAAc;EACd,eAAe;AACjB;;AAEA;EACE,wBAAwB;EACxB,4BAA4B;EAC5B,sCAAsC;EACtC,iCAAiC;AACnC;;AAEA;EACE,8BAA8B;AAChC;;AAEA;;oDAEoD;AACpD;;EAEE,SAAS;EACT,aAAa;EACb,SAAS;EACT,UAAU;EACV,UAAU;EACV,gBAAgB;EAChB,oBAAoB;EACpB,sFAAsF;AACxF;;AAEA;EACE,UAAU;EACV,uBAAuB;EACvB,0BAA0B;AAC5B;;AAEA,0EAA0E;AAC1E;EACE,SAAS;EACT,cAAc;EACd,gBAAgB;EAChB,UAAU;AACZ;;AAEA;sDACsD;AACtD;EACE,aAAa;AACf;;AAEA;EACE;;IAEE,gBAAgB;EAClB;AACF;;AAEA;;;;;;uDAMuD;AACvD;;EAEE,YAAY;EACZ,gBAAgB;EAChB,uBAAuB;EACvB,gBAAgB;EAChB,UAAU;EACV,YAAY;EACZ,aAAa;AACf;;AAEA;;EAEE,YAAY;EACZ,gBAAgB;AAClB;;AAEA,+DAA+D;AAC/D;EACE,cAAc;EACd,oBAAoB;EACpB,oBAAoB;EACpB,qBAAqB;EACrB,8BAA8B;AAChC","sourcesContent":["/* ════════════════════════════════════════════════════════════════════════\n   Top app bar\n\n   SubPageHeader (App.js) renders, in source order:\n\n     .sub-page-header-inner\n       .sph-left   → logo mark, \"AcyMart\", the user's first name, \"›\", title\n       .sph-right  → user name again, theme toggle, [← Back] pill\n\n   On the web that's a branded sub-header. On a phone it reads as a wasted\n   72px strip with the primary navigation control stranded in the far corner\n   and the title buried behind three pieces of branding.\n\n   Rather than fork the component, this restyles it into a standard app bar —\n   back affordance at the leading edge, one title, actions trailing:\n\n     [←]  Order History                              [◑]\n\n   `display: contents` on .sph-right dissolves that wrapper so its children\n   become flex items of the same row as .sph-left, which is what lets `order`\n   pull the back button ahead of the title without touching the JSX.\n   ════════════════════════════════════════════════════════════════════════ */\n\nhtml.m-app .sub-page-header {\n  position: sticky;\n  top: 0;\n  z-index: 120;\n  background: var(--m-surface-bar);\n  border-bottom: 1px solid var(--m-hairline);\n  box-shadow: none;\n  /* Claims the status-bar strip in the packaged app so the bar's content\n     starts below the clock, and the bar's own background paints behind it. */\n  padding-top: var(--m-safe-top);\n  padding-inline: 0;\n}\n\n@supports (backdrop-filter: blur(12px)) {\n  html.m-app .sub-page-header {\n    background: color-mix(in srgb, var(--m-surface-bar) 82%, transparent);\n    backdrop-filter: saturate(180%) blur(16px);\n    -webkit-backdrop-filter: saturate(180%) blur(16px);\n  }\n}\n\nhtml.m-app .sub-page-header-inner {\n  display: flex;\n  align-items: center;\n  gap: var(--m-1);\n  min-height: var(--m-appbar);\n  max-width: none;\n  margin: 0;\n  padding: 0 var(--m-2) 0 var(--m-1);\n  padding-left: max(var(--m-1), var(--m-safe-left));\n  padding-right: max(var(--m-2), var(--m-safe-right));\n}\n\n/* Dissolve the right-hand group so back / theme / title share one flex row. */\nhtml.m-app .sph-right {\n  display: contents;\n}\n\n/* ── Back: the leading icon button ────────────────────────────────────── */\nhtml.m-app .sph-back {\n  order: 1;\n  flex: 0 0 auto;\n  display: inline-flex;\n  align-items: center;\n  justify-content: center;\n  width: var(--m-touch);\n  height: var(--m-touch);\n  min-width: var(--m-touch);\n  min-height: var(--m-touch);\n  padding: 0;\n  gap: 0;\n  border: none;\n  border-radius: var(--m-r-pill);\n  background: transparent;\n  color: var(--color-text-primary);\n  /* Collapses the \"Back\" / \"Back to Cart\" text label without hiding the\n     inline <svg>, which keeps the accessible name intact for screen\n     readers while the icon alone carries it visually. */\n  font-size: 0;\n  line-height: 0;\n  white-space: nowrap;\n  overflow: hidden;\n}\n\nhtml.m-app .sph-back svg {\n  width: var(--m-icon-nav);\n  height: var(--m-icon-nav);\n  stroke-width: 2;\n  flex: 0 0 auto;\n}\n\nhtml.m-app .sph-back:active {\n  background: var(--color-surface-hover);\n}\n\n/* ── Title ──────────────────────────────────────────────────────────────── */\nhtml.m-app .sph-left {\n  order: 2;\n  display: flex;\n  align-items: center;\n  min-width: 0;      /* lets the title ellipsis instead of pushing actions off */\n  flex: 1 1 auto;\n  gap: 0;\n  margin: 0;\n  padding: 0;\n}\n\n/* Branding and the breadcrumb machinery have no job here: the app icon is on\n   the home screen, and the user's name is one tap away under Account. */\nhtml.m-app .sph-logo-icon-wrap,\nhtml.m-app .sph-logo-name,\nhtml.m-app .sph-sep,\nhtml.m-app .sph-mobile-user,\nhtml.m-app .sph-mobile-username,\nhtml.m-app .sph-user {\n  display: none;\n}\n\nhtml.m-app .sph-title {\n  font-size: 1.0625rem;             /* 17px — the app-bar size, between  */\n  font-weight: var(--m-fw-semibold); /* card title and page title        */\n  line-height: var(--m-lh-tight);\n  letter-spacing: var(--m-track-title);\n  color: var(--color-text-primary);\n  white-space: nowrap;\n  overflow: hidden;\n  text-overflow: ellipsis;\n  min-width: 0;\n  /* Optical alignment: sits 2px in from the back button's icon so title and\n     content below share a left edge. */\n  padding-left: var(--m-1);\n}\n\n/* When there's a back button the title indents past it; when there isn't,\n   it takes the full gutter itself. */\nhtml.m-app .sub-page-header-inner:not(:has(.sph-back)) .sph-left {\n  padding-left: var(--m-3);\n}\n\n/* ── Trailing actions ───────────────────────────────────────────────────── */\nhtml.m-app .sph-theme {\n  order: 3;\n  flex: 0 0 auto;\n  display: inline-flex;\n  align-items: center;\n  justify-content: center;\n  width: var(--m-touch);\n  height: var(--m-touch);\n  min-width: var(--m-touch);\n  padding: 0;\n  border: none;\n  border-radius: var(--m-r-pill);\n  background: transparent;\n  color: var(--color-text-secondary);\n}\n\nhtml.m-app .sph-theme:active {\n  background: var(--color-surface-hover);\n}\n\nhtml.m-app .sph-theme svg {\n  width: var(--m-icon); height: var(--m-icon);\n}\n\n/* `hideThemeOnMobile` is passed on most sub-pages; honour it so the bar\n   carries a back affordance and a title and nothing else. */\nhtml.m-app .sph-theme-hide-mobile {\n  display: none;\n}\n\nhtml.m-app .sub-page-header-flush {\n  border-bottom: none;\n}\n\n/* ════════════════════════════════════════════════════════════════════════\n   Storefront header\n\n   The home screen's header is a different component (.site-header) carrying\n   an announcement bar, a logo/tagline lockup, a search field with a category\n   <select> welded into it, and a row of icon actions — roughly 190px of\n   chrome before any product. Compressed onto a phone it pushed the first\n   product below the fold.\n\n   Mobile keeps exactly two things in the bar: identity (so you know where\n   you are) and search (the primary way anyone finds anything in a store).\n   ════════════════════════════════════════════════════════════════════════ */\n\nhtml.m-app .site-header {\n  position: sticky;\n  top: 0;\n  z-index: 120;\n}\n\n/* The announcement bar wrapped to two lines and ate 100px. It carries real\n   information (flash sale, free-delivery threshold), so it stays — but as a\n   single non-wrapping marquee line, and never inside the packaged app where\n   there's no room to spare. */\nhtml.m-app .ann-bar {\n  padding-top: var(--m-safe-top);\n  background: var(--color-brand-navy);\n}\n\nhtml.m-app .ann-bar-inner {\n  min-height: 30px;\n  padding: 0 var(--m-gutter);\n  gap: var(--m-2);\n}\n\nhtml.m-app .ann-text {\n  font-size: var(--m-fs-caption);\n  line-height: 1.2;\n  font-weight: var(--m-fw-medium);\n  color: var(--color-on-navy);\n  white-space: nowrap;\n  overflow: hidden;\n  text-overflow: ellipsis;\n  margin: 0;\n}\n\nhtml.m-app .ann-countdown {\n  font-variant-numeric: tabular-nums;\n  font-weight: var(--m-fw-semibold);\n}\n\n/* Sign-in lives in the Account tab; a second entry point here competes with\n   it and costs width the sale message needs. */\nhtml.m-app .ann-right,\nhtml.m-app .ann-auth-btn {\n  display: none;\n}\n\n/* .m-installed, not .m-native: an installed PWA is launched from the same home\n   screen with the same no-browser-chrome framing, so the announcement bar is\n   just as out of place there as in the packaged app. */\nhtml.m-installed .ann-bar {\n  display: none;\n}\n\nhtml.m-app .main-navbar {\n  background: var(--m-surface-bar);\n  border-bottom: 1px solid var(--m-hairline);\n  box-shadow: none;\n  padding: 0;\n}\n\nhtml.m-app .nb-top {\n  display: flex;\n  align-items: center;\n  gap: var(--m-2);\n  min-height: var(--m-appbar);\n  padding: var(--m-2) var(--m-gutter);\n  max-width: none;\n}\n\nhtml.m-app .nb-logo {\n  flex: 0 0 auto;\n  gap: var(--m-2);\n  padding: 0;\n  min-height: 0;\n}\n\n/* The tagline is brand copy for a wide header; on a phone it's a second line\n   of text competing with the search field for the same row. */\nhtml.m-app .nb-logo-tagline {\n  display: none;\n}\n\nhtml.m-app .nb-logo-name {\n  font-size: var(--m-fs-card-title);\n  font-weight: var(--m-fw-bold);\n  letter-spacing: var(--m-track-title);\n  line-height: 1;\n}\n\nhtml.m-app .nb-logo svg {\n  width: 30px;\n  height: 30px;\n}\n\n/* Collapsed search: an icon button that expands the field over the bar. */\nhtml.m-app .nb-mobile-search-btn {\n  order: 5;\n  flex: 0 0 auto;\n  display: inline-flex;\n  align-items: center;\n  justify-content: center;\n  width: var(--m-touch);\n  height: var(--m-touch);\n  min-width: var(--m-touch);\n  padding: 0;\n  border: none;\n  border-radius: var(--m-r-pill);\n  background: var(--m-surface-sunken);\n  color: var(--color-text-primary);\n}\n\nhtml.m-app .nb-mobile-search-btn:active {\n  background: var(--color-surface-hover);\n}\n\nhtml.m-app .nb-actions {\n  order: 6;\n  display: flex;\n  align-items: center;\n  gap: var(--m-1);\n  margin-left: auto;\n}\n\n/* Cart, wishlist and account all have bottom-nav tabs — duplicating them in\n   the top bar is the single biggest source of \"too many actions competing\"\n   on this screen. Theme and language stay: neither has another home. */\nhtml.m-app .nb-actions > .nb-cart-btn,\nhtml.m-app .nb-actions > .nb-wishlist-btn,\nhtml.m-app .nb-actions > .nb-account-wrap {\n  display: none;\n}\n\nhtml.m-app .nb-actions button {\n  width: var(--m-touch);\n  height: var(--m-touch);\n  min-width: var(--m-touch);\n  padding: 0;\n  border-radius: var(--m-r-pill);\n}\n\n/* ── Search field ───────────────────────────────────────────────────────── */\nhtml.m-app .nb-search-wrap {\n  order: 4;\n  flex: 1 1 auto;\n  min-width: 0;\n}\n\n/* The field itself stays collapsed until the search icon expands it — that\n   behaviour already exists in App.css and is the right one, so `display` is\n   deliberately NOT set here. Setting it would show the field and the icon\n   button that opens it at the same time, which is how this bar ended up\n   with two search affordances side by side. Only the appearance of the\n   expanded state is restyled. */\nhtml.m-app .nb-search-bar {\n  align-items: center;\n  gap: var(--m-2);\n  height: var(--m-touch);\n  padding: 0 var(--m-3);\n  border: 1px solid var(--m-hairline);\n  border-radius: var(--m-r-pill);\n  background: var(--m-surface-sunken);\n  box-shadow: none;\n}\n\nhtml.m-app .nb-search-bar:focus-within {\n  border-color: var(--color-secondary);\n  background: var(--m-surface-card);\n}\n\nhtml.m-app .nb-sb-input {\n  flex: 1 1 auto;\n  min-width: 0;\n  height: 100%;\n  border: none;\n  background: transparent;\n  /* 16px exactly: iOS Safari zooms the viewport on focus for anything\n     smaller, and never zooms back out. */\n  font-size: 1rem;\n  color: var(--color-text-primary);\n  padding: 0;\n}\n\nhtml.m-app .nb-sb-input::placeholder {\n  color: var(--color-text-muted);\n  font-size: var(--m-fs-body);\n}\n\nhtml.m-app .nb-sb-search-icon {\n  flex: 0 0 auto;\n  color: var(--color-text-muted);\n  width: var(--m-icon); height: var(--m-icon);\n}\n\n/* The welded-in category <select>, its divider, its caret and the submit\n   button are a desktop compound control. On mobile, category selection is\n   the job of the category rail below the bar and the Categories tab, and\n   the keyboard's own \"Search\" key submits. */\nhtml.m-app .nb-sb-cat,\nhtml.m-app .nb-sb-divider,\nhtml.m-app .nb-sb-caret,\nhtml.m-app .nb-sb-btn {\n  display: none;\n}\n\nhtml.m-app .nb-sb-clear {\n  flex: 0 0 auto;\n  display: inline-flex;\n  align-items: center;\n  justify-content: center;\n  width: 22px;\n  height: 22px;\n  min-height: 22px;\n  min-width: 22px;\n  padding: 0;\n  border: none;\n  border-radius: var(--m-r-pill);\n  background: var(--color-surface-hover);\n  color: var(--color-text-secondary);\n}\n\n/* Expanded search takes the whole bar — the standard mobile search overlay,\n   with the back chevron returning to browse. */\nhtml.m-app .nb-search-expanded {\n  background: var(--m-surface-card);\n}\n\nhtml.m-app .nb-sb-back {\n  flex: 0 0 auto;\n  display: inline-flex;\n  align-items: center;\n  justify-content: center;\n  width: 32px;\n  height: 32px;\n  min-height: 32px;\n  min-width: 32px;\n  margin-left: calc(var(--m-2) * -1);\n  padding: 0;\n  border: none;\n  background: transparent;\n  color: var(--color-text-primary);\n}\n\n/* ── Search suggestions ─────────────────────────────────────────────────\n   A dropdown panel becomes a full-width results list flush under the bar. */\nhtml.m-app .nb-autocomplete {\n  position: absolute;\n  left: calc(var(--m-gutter) * -1);\n  right: calc(var(--m-gutter) * -1);\n  top: calc(100% + var(--m-2));\n  max-height: 60vh;\n  overflow-y: auto;\n  border: none;\n  border-top: 1px solid var(--m-hairline);\n  border-radius: 0;\n  box-shadow: var(--m-shadow-raised);\n  background: var(--m-surface-card);\n  padding: var(--m-1) 0;\n}\n\nhtml.m-app .nb-ac-item {\n  display: flex;\n  align-items: center;\n  gap: var(--m-3);\n  width: 100%;\n  min-height: 56px;\n  padding: var(--m-2) var(--m-gutter);\n  border: none;\n  background: transparent;\n  text-align: left;\n}\n\nhtml.m-app .nb-ac-item:active {\n  background: var(--color-surface-hover);\n}\n\nhtml.m-app .nb-ac-img {\n  width: 40px;\n  height: 40px;\n  border-radius: var(--m-r-xs);\n  object-fit: cover;\n  flex: 0 0 auto;\n}\n\nhtml.m-app .nb-ac-info {\n  min-width: 0;\n  flex: 1 1 auto;\n}\n\nhtml.m-app .nb-ac-name {\n  display: block;\n  font-size: var(--m-fs-body);\n  font-weight: var(--m-fw-medium);\n  line-height: var(--m-lh-snug);\n  white-space: nowrap;\n  overflow: hidden;\n  text-overflow: ellipsis;\n}\n\nhtml.m-app .nb-ac-cat {\n  font-size: var(--m-fs-caption);\n  color: var(--color-text-muted);\n}\n\nhtml.m-app .nb-ac-price {\n  flex: 0 0 auto;\n  font-size: var(--m-fs-secondary);\n  font-weight: var(--m-fw-semibold);\n  font-variant-numeric: tabular-nums;\n}\n\n/* ── Storefront stats bar: hidden on mobile ─────────────────────────────\n   \"4+ Verified Sellers · 98% Positive Reviews · 140 Total Reviews · Fast\n   Delivery\" is social proof aimed at a first-time desktop visitor. On a\n   phone it costs 138px above the product grid — most of a screen — and it\n   is the only element in the app that runs an infinite marquee animation,\n   which spends battery for something nobody scrolls back up to read.\n\n   `display: none` on the wrapper takes the animation with it, so the\n   marquee stops running rather than merely being off-screen. The markup\n   still renders for the desktop build, which is untouched. */\nhtml.m-app .shop-stats-bar {\n  display: none;\n}\n\n/* ── Search button in the top bar ───────────────────────────────────────\n   All four top-bar actions are 44x44 for touch, but this one carried a\n   filled circle while its siblings (language, theme) are bare icons — so\n   it read as a big grey blob next to two small glyphs.\n\n   The fill comes off and the icon settles at the same 20px as its\n   neighbours, which is what actually makes it look smaller. The 44px box\n   stays: it is the tap target, and with no background it is invisible, so\n   shrinking it would cost accuracy and buy nothing visually. */\nhtml.m-app .nb-mobile-search-btn {\n  background: transparent;\n  border: none;\n}\n\nhtml.m-app .nb-mobile-search-btn:active {\n  background: var(--color-surface-hover);\n}\n\n/* One icon size across the whole action row, so search, language and theme\n   sit on a common optical weight. */\nhtml.m-app .nb-mobile-search-btn svg,\nhtml.m-app .nb-actions button svg {\n  width: 20px;\n  height: 20px;\n}\n\n/* ════════════════════════════════════════════════════════════════════════\n   Bottom navigation: labels and icons in black\n\n   An explicit, requested exception to this layer's rule of leaving\n   BottomNav alone. The geometry fence above still stands -- only colour\n   changes here, nothing that affects size or spacing.\n\n   --color-text-primary rather than a literal black: it is navy (#010D21) in\n   light mode, which is the black being asked for, and flips to white in dark\n   mode. A hard-coded black would be invisible against the dark bar, and the\n   four-colour system has no separate black to reach for anyway.\n\n   The icons inherit this: BottomNav renders its SVGs with stroke and fill\n   set to currentColor, so setting the item's colour moves both.\n\n   The active tab used to be the only coloured thing in the bar, which is how\n   it announced itself. With everything the same colour that cue is gone, so\n   it moves onto weight -- and the Home tab already swaps to a filled icon\n   when active, so the state is carried by shape as well as weight rather\n   than by colour alone.\n   ════════════════════════════════════════════════════════════════════════ */\n\nhtml.m-app .bn-item,\nhtml.m-app .bn-item.active {\n  color: var(--color-text-primary);\n}\n\nhtml.m-app .bn-item.active .bn-label {\n  font-weight: 700;\n}\n\n/* ════════════════════════════════════════════════════════════════════════\n   Separating the app bar from the mobile top bar\n\n   Two different components sit back to back on the storefront:\n\n     .main-navbar   the app bar -- logo, search, language, theme\n     .mtb-wrap      the mobile top bar -- delivery address, category rail\n\n   They are already siblings in the DOM, but visually they were one object:\n   .main-navbar ended at y=91 and .mtb-wrap began at y=91, both pure white,\n   with hairlines between them that read as internal dividers of a single\n   201px slab rather than as the edges of two separate things.\n\n   The fix is a band of page canvas between them. The app bar keeps a shadow\n   so it reads as fixed chrome; the top bar becomes a block of page content\n   that happens to sit directly under it -- which is also what it behaves\n   like, since the app bar is sticky and the top bar scrolls away.\n   ════════════════════════════════════════════════════════════════════════ */\n\nhtml.m-app .main-navbar {\n  border-bottom: 1px solid var(--m-hairline);\n  box-shadow: var(--m-shadow-sm);\n}\n\nhtml.m-app .mtb-wrap {\n  /* The gap is the separation -- page canvas showing through between the\n     two surfaces. */\n  margin-top: var(--m-2);\n  border-top: none;\n  border-bottom: 1px solid var(--m-hairline);\n  background: var(--m-surface-card);\n}\n\n/* The delivery row is the top edge of that block now, so its own top border\n   would double up with the block's. */\nhtml.m-app .mtb-delivery-row {\n  border-top: none;\n}\n\n/* And the category rail is the bottom edge, so the same applies there. */\nhtml.m-app .mtb-cat-strip {\n  border-bottom: none;\n}\n\n/* ════════════════════════════════════════════════════════════════════════\n   Persistent search field + condensed header on scroll\n\n   Previously the phone header hid the search behind a magnifier that\n   expanded it over the bar. That is one tap between a shopper and the single\n   most-used control in a store. The field is now always present, on its own\n   full-width row beneath the logo -- the shape in the reference.\n\n   On scroll past the logo (see initCondensedHeader in mobileUI.js) the logo\n   row folds away and the search field becomes the sticky header by itself.\n   The logo is not sticky, which is the point: it has already done its job by\n   the time you are three screens into a product list.\n   ════════════════════════════════════════════════════════════════════════ */\n\n/* The bar becomes two rows: logo + actions, then the search field. */\nhtml.m-app .nb-top {\n  flex-wrap: wrap;\n  row-gap: var(--m-2);\n  padding-bottom: var(--m-3);\n}\n\n/* Always shown -- App.css hides it below 768px until expanded, which is the\n   behaviour being replaced here. */\nhtml.m-app .nb-search-bar {\n  display: flex;\n}\n\n/* The magnifier that used to expand the field, and the back chevron inside\n   the expanded state, are both redundant once the field is permanent. */\nhtml.m-app .nb-mobile-search-btn,\nhtml.m-app .nb-sb-back {\n  display: none;\n}\n\n/* Full width, second row. */\nhtml.m-app .nb-search-wrap {\n  order: 10;\n  flex: 1 0 100%;\n  min-width: 100%;\n}\n\nhtml.m-app .nb-search-bar {\n  height: var(--m-control);\n  border-radius: var(--m-r-sm);\n  border-color: var(--m-hairline-strong);\n  background: var(--m-surface-card);\n}\n\nhtml.m-app .nb-sb-input::placeholder {\n  color: var(--color-text-muted);\n}\n\n/* ── Condensed state ────────────────────────────────────────────────────\n   Height/opacity rather than display:none so it folds instead of jumping,\n   and so the transition has something to animate. */\nhtml.m-app.m-condensed .nb-logo,\nhtml.m-app.m-condensed .nb-actions {\n  height: 0;\n  min-height: 0;\n  margin: 0;\n  padding: 0;\n  opacity: 0;\n  overflow: hidden;\n  pointer-events: none;\n  transition: height var(--m-dur) var(--m-ease), opacity var(--m-dur-fast) var(--m-ease);\n}\n\nhtml.m-app.m-condensed .nb-top {\n  row-gap: 0;\n  padding-top: var(--m-2);\n  padding-bottom: var(--m-2);\n}\n\n/* The announcement bar is the other thing worth its space only at rest. */\nhtml.m-app.m-condensed .ann-bar {\n  height: 0;\n  padding-top: 0;\n  overflow: hidden;\n  opacity: 0;\n}\n\n/* The category rail stays: it is navigation, and it is what makes the\n   condensed header useful rather than just smaller. */\nhtml.m-app.m-condensed .mtb-wrap {\n  margin-top: 0;\n}\n\n@media (prefers-reduced-motion: reduce) {\n  html.m-app.m-condensed .nb-logo,\n  html.m-app.m-condensed .nb-actions {\n    transition: none;\n  }\n}\n\n/* The field inside the search bar must not draw its own box.\n   forms.css styles `input[type=\"search\"]` generically, and that selector --\n   html + .m-app + input[type=search] -- scores (0,2,2), which outranks a\n   plain `.nb-sb-input` at (0,2,1). The result was a bordered input nested\n   inside the bordered bar: two rounded rectangles, one inside the other,\n   with the placeholder pushed in past the magnifier. Scoping to the bar\n   raises this to (0,3,1) so the field is flat again. */\nhtml.m-app .nb-search-bar .nb-sb-input,\nhtml.m-app .nb-search-bar input[type=\"search\"] {\n  border: none;\n  border-radius: 0;\n  background: transparent;\n  box-shadow: none;\n  padding: 0;\n  height: 100%;\n  min-height: 0;\n}\n\nhtml.m-app .nb-search-bar .nb-sb-input:focus,\nhtml.m-app .nb-search-bar input[type=\"search\"]:focus {\n  border: none;\n  box-shadow: none;\n}\n\n/* Keep the magnifier visible at the leading edge of the bar. */\nhtml.m-app .nb-search-bar .nb-sb-search-icon {\n  flex: 0 0 auto;\n  display: inline-flex;\n  width: var(--m-icon);\n  height: var(--m-icon);\n  color: var(--color-text-muted);\n}\n"],"sourceRoot":""}]);
// Exports
___CSS_LOADER_EXPORT___.locals = {};
export default ___CSS_LOADER_EXPORT___;
